import { Archive, ArchiveRestore, Check, FileDown, Info, Link, Paperclip, X } from "lucide-react";
import {
  useState,
  useRef,
  useEffect,
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
  type PointerEvent,
} from "react";

import { useDictation } from "@/hooks/useDictation";
import { useSession, useCreateJob, useSetSessionHidden } from "@/hooks/useSession";
import { uploadAttachments } from "@/lib/api";
import { formatAttachmentSize, mergeAttachmentFiles } from "@/lib/attachments";
import { formatSessionTitle } from "@/lib/session-title";
import { sessionToMarkdown } from "@/lib/session-to-markdown";
import { useStore } from "@/lib/store";

import { ConversationMessage } from "./ConversationMessage";
import { DictationButton } from "./DictationButton";
import { DictationStatus } from "./DictationStatus";
import { Hint } from "./Hint";
import { SendButton } from "./SendButton";
import { SidebarToggleButton } from "./SidebarToggleButton";
import { formatTokens, getModelLabel } from "./stats/formatters";
import { Tooltip } from "./Tooltip";

interface AgentBadgeProps {
  agentId: string;
}

function AgentBadge({ agentId }: AgentBadgeProps): React.JSX.Element {
  const isPlan = agentId === "plan";
  const colorClasses = isPlan
    ? "bg-cyber-cyan/10 text-cyber-cyan border-cyber-cyan/30"
    : "bg-cyber-magenta/10 text-cyber-magenta border-cyber-magenta/30";

  return (
    <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${colorClasses}`}>
      {agentId.toUpperCase()}
    </span>
  );
}

function autoResizeTextarea(textarea: HTMLTextAreaElement): void {
  textarea.style.height = "auto";
  textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
}

export function ConversationView(): React.JSX.Element | null {
  const activeSessionKey = useStore((state) => state.activeSessionKey);
  const showToast = useStore((state) => state.showToast);

  const { data, isLoading } = useSession(activeSessionKey);
  const createJob = useCreateJob();
  const setSessionHidden = useSetSessionHidden(activeSessionKey);

  const [replyMessage, setReplyMessage] = useState("");
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [isUploadingReply, setIsUploadingReply] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedMarkdown, setCopiedMarkdown] = useState(false);
  const conversationRef = useRef<HTMLDivElement>(null);
  const followActivityRef = useRef(true);
  const scrollSessionRef = useRef(activeSessionKey);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  const replyFileInputRef = useRef<HTMLInputElement>(null);
  const dictation = useDictation({
    onTranscript: (text) => {
      insertReplyTranscriptAtCursor(text);
    },
    onError: (errorMessage) => {
      showToast(errorMessage, "error");
    },
  });

  const isReplyRecording = dictation.status === "recording";
  const isReplyProcessingAudio =
    dictation.status === "transcribing" || dictation.status === "processing";
  const isReplySubmitting = createJob.isPending || isUploadingReply;

  // Check if any active job in the session has a pending question — if so,
  // the composer should be disabled because the session is busy waiting for
  // the user to answer via the QuestionPrompt UI. Cancelled/failed/completed
  // jobs with stale pendingQuestion metadata should not block the composer.
  const hasPendingQuestion = Boolean(
    data?.jobs.some((j) => {
      if (j.status === "cancelled" || j.status === "failed" || j.status === "completed") {
        return false;
      }
      const meta = j.metadata as { pendingQuestion?: unknown } | null;
      return meta?.pendingQuestion != null;
    }),
  );

  const isReplyBusy = isReplySubmitting || isReplyProcessingAudio || hasPendingQuestion;

  async function copyLink(): Promise<void> {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    showToast("Link copied", "success");
    setTimeout(() => setCopied(false), 2000);
  }

  async function copyAsMarkdown(): Promise<void> {
    if (!data) return;
    await navigator.clipboard.writeText(sessionToMarkdown(data));
    setCopiedMarkdown(true);
    showToast("Markdown copied", "success");
    setTimeout(() => setCopiedMarkdown(false), 2000);
  }

  async function toggleSessionHidden(): Promise<void> {
    if (!data) return;

    try {
      await setSessionHidden.mutateAsync(!data.hidden);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update conversation";
      showToast(errorMessage, "error");
    }
  }

  // Follow live progress only while at the bottom; don't pull readers away from earlier activity.
  useEffect(() => {
    if (scrollSessionRef.current !== activeSessionKey) {
      followActivityRef.current = true;
      scrollSessionRef.current = activeSessionKey;
    }
    if (conversationRef.current && followActivityRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [activeSessionKey, data?.jobs]);

  useEffect(() => {
    if (isReplyBusy) return;
    const id = window.setTimeout(() => {
      replyInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [activeSessionKey, isReplyBusy]);

  useEffect(() => {
    const textarea = replyInputRef.current;
    if (!textarea) return;
    autoResizeTextarea(textarea);
  }, [replyMessage]);

  if (!activeSessionKey) return null;

  async function handleReply(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!replyMessage.trim() || isReplyBusy || isReplyRecording) return;

    try {
      let attachmentIds: string[] | undefined;
      if (replyFiles.length > 0) {
        setIsUploadingReply(true);
        const uploadResponse = await uploadAttachments(replyFiles);
        attachmentIds = uploadResponse.attachments.map((attachment) => attachment.id);
      }

      await createJob.mutateAsync({
        message: replyMessage.trim(),
        threadKey: activeSessionKey ?? undefined,
        attachmentIds,
      });
      setReplyMessage("");
      setReplyFiles([]);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to send reply";
      showToast(errorMessage, "error");
    } finally {
      setIsUploadingReply(false);
    }
  }

  function insertReplyTranscriptAtCursor(transcript: string): void {
    const normalizedTranscript = transcript.trim();
    if (!normalizedTranscript) {
      return;
    }

    const textarea = replyInputRef.current;
    if (!textarea) {
      setReplyMessage((current) =>
        current.trim().length > 0 ? `${current} ${normalizedTranscript}` : normalizedTranscript,
      );
      return;
    }

    const selectionStart = textarea.selectionStart ?? textarea.value.length;
    const selectionEnd = textarea.selectionEnd ?? textarea.value.length;

    setReplyMessage((current) => {
      const start = Math.min(selectionStart, current.length);
      const end = Math.min(selectionEnd, current.length);
      const before = current.slice(0, start);
      const after = current.slice(end);
      const prefix = before.length > 0 && !/\s$/.test(before) ? " " : "";
      const suffix = after.length > 0 && !/^\s/.test(after) ? " " : "";
      const insertion = `${prefix}${normalizedTranscript}${suffix}`;
      const nextMessage = `${before}${insertion}${after}`;
      const nextCursor = before.length + insertion.length;

      requestAnimationFrame(() => {
        const target = replyInputRef.current;
        if (!target) return;
        target.focus();
        target.selectionStart = nextCursor;
        target.selectionEnd = nextCursor;
        autoResizeTextarea(target);
      });

      return nextMessage;
    });
  }

  function focusReplyInput(): void {
    replyInputRef.current?.focus();
  }

  function handleReplyContainerPointerDown(e: PointerEvent<HTMLDivElement>): void {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest("input,textarea,button,a,select")) return;
    e.preventDefault();
    focusReplyInput();
  }

  function addReplyFiles(incomingFiles: File[]): void {
    const { files, error } = mergeAttachmentFiles(replyFiles, incomingFiles);
    if (error) {
      showToast(error, "error");
      return;
    }
    setReplyFiles(files);
  }

  function handleReplyFileChange(e: ChangeEvent<HTMLInputElement>): void {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      addReplyFiles(files);
    }
    e.target.value = "";
  }

  function handleReplyPaste(e: ClipboardEvent<HTMLTextAreaElement>): void {
    const files = Array.from(e.clipboardData.files || []);
    if (files.length === 0) {
      return;
    }

    e.preventDefault();
    addReplyFiles(files);
  }

  function handleReplyDragOver(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setIsDraggingFiles(true);
  }

  function handleReplyDragLeave(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) {
      return;
    }
    setIsDraggingFiles(false);
  }

  function handleReplyDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setIsDraggingFiles(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      addReplyFiles(files);
    }
  }

  function removeReplyFile(index: number): void {
    setReplyFiles((current) => current.filter((_, currentIndex) => currentIndex !== index));
  }

  function handleReplyKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key !== "Enter") {
      return;
    }

    if (e.ctrlKey) {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const nextMessage = `${replyMessage.slice(0, start)}\n${replyMessage.slice(end)}`;
      setReplyMessage(nextMessage);
      requestAnimationFrame(() => {
        textarea.selectionStart = start + 1;
        textarea.selectionEnd = start + 1;
        autoResizeTextarea(textarea);
      });
      return;
    }

    if (e.shiftKey) {
      return;
    }

    e.preventDefault();
    if (replyMessage.trim() && !isReplyBusy && !isReplyRecording) {
      void handleReply(e as unknown as FormEvent);
    }
  }

  const firstMessage = data?.jobs?.[0]?.messageText;
  const fallbackTitle = firstMessage ? `${firstMessage.slice(0, 50)}...` : "Session";
  const rawTitle = data?.sessionTitle ?? fallbackTitle;
  const title = formatSessionTitle(rawTitle);
  const sessionUsage = data?.usage;
  const headerInfo = (
    <div className="space-y-1 text-left">
      <div className="text-cyber-muted text-[10px] uppercase">
        {data?.source ?? "..."} · {data?.jobs.length ?? 0} message
        {(data?.jobs.length ?? 0) !== 1 ? "s" : ""}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {data && data.origin !== "interactive" && (
          <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-400">
            CIRCUIT
          </span>
        )}
        {data?.agentId && <AgentBadge agentId={data.agentId} />}
        {data?.modelId && (
          <span className="text-cyber-muted font-mono text-[10px]">
            {data.modelId.split("/").pop()}
          </span>
        )}
      </div>
      {sessionUsage && sessionUsage.responses > 0 && (
        <div className="space-y-1">
          <div className="text-cyber-muted/80 text-[10px] tracking-wide uppercase">
            {formatTokens(sessionUsage.totalTokens)} tokens ·{" "}
            <span className="text-cyber-cyan">
              {formatTokens(sessionUsage.outputTokens)} output
            </span>{" "}
            · {sessionUsage.responses} response
            {sessionUsage.responses === 1 ? "" : "s"}
          </div>
          <div className="flex flex-wrap gap-1">
            {sessionUsage.byModel.slice(0, 3).map((row) => (
              <span
                key={`${row.providerId}/${row.modelId}`}
                className="border-cyber-border bg-cyber-surface/70 text-cyber-muted rounded border px-1.5 py-0.5 text-[10px]"
                title={`${row.providerId}/${row.modelId} — ${formatTokens(row.totalTokens)} total, ${formatTokens(row.outputTokens)} output`}
              >
                {getModelLabel(row.providerId, row.modelId, row.modelName)} ·{" "}
                {formatTokens(row.outputTokens)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  useEffect(() => {
    document.title = title;
    return () => {
      document.title = "Sherlog";
    };
  }, [title]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <div className="border-cyber-border flex-shrink-0 border-b py-2 pr-2 pl-0 md:px-4 md:py-3">
        <div className="flex items-center justify-between gap-2 px-2 md:gap-3 md:px-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <SidebarToggleButton />
            <h2 className="text-cyber-text min-w-0 flex-1 truncate text-sm font-medium md:max-w-[320px] md:flex-none md:flex-shrink-0">
              {title}
            </h2>
          </div>
          <div className="flex items-center gap-1">
            <Hint content={headerInfo}>
              <button
                type="button"
                className="text-cyber-muted hover:bg-cyber-cyan/10 hover:text-cyber-cyan rounded p-1.5 transition-all md:p-2"
                aria-label="Session details"
                title="Session details"
              >
                <Info size={16} />
              </button>
            </Hint>
            <Tooltip content="Copy as Markdown" placement="bottom">
              <button
                onClick={copyAsMarkdown}
                className="text-cyber-muted hover:bg-cyber-cyan/10 hover:text-cyber-cyan rounded p-1.5 transition-all md:p-2"
                title="Copy as Markdown"
                aria-label="Copy as Markdown"
              >
                {copiedMarkdown ? <Check size={16} /> : <FileDown size={16} />}
              </button>
            </Tooltip>
            <Tooltip content="Copy link" placement="bottom">
              <button
                onClick={copyLink}
                className="text-cyber-muted hover:bg-cyber-cyan/10 hover:text-cyber-cyan rounded p-1.5 transition-all md:p-2"
                title="Copy link"
                aria-label="Copy link"
              >
                {copied ? <Check size={16} /> : <Link size={16} />}
              </button>
            </Tooltip>
            <Tooltip
              content={data?.hidden ? "Show in sidebar" : "Hide from sidebar"}
              placement="bottom"
            >
              <button
                type="button"
                onClick={toggleSessionHidden}
                disabled={!data || setSessionHidden.isPending}
                className="text-cyber-muted hover:bg-cyber-cyan/10 hover:text-cyber-cyan rounded p-1.5 transition-all disabled:cursor-not-allowed disabled:opacity-50 md:p-2"
                title={data?.hidden ? "Show in sidebar" : "Hide from sidebar"}
                aria-label={data?.hidden ? "Show in sidebar" : "Hide from sidebar"}
              >
                {data?.hidden ? <ArchiveRestore size={16} /> : <Archive size={16} />}
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Conversation */}
      <div
        ref={conversationRef}
        className="flex-1 overflow-y-auto overscroll-contain p-4"
        onScroll={(event) => {
          const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
          followActivityRef.current = scrollHeight - scrollTop - clientHeight < 80;
        }}
      >
        <div className="mx-auto max-w-3xl space-y-4">
          {isLoading ? (
            <div className="text-cyber-muted py-8 text-center">
              <span className="animate-spin-glow mr-2 inline-block">◐</span>
              Loading conversation...
            </div>
          ) : (
            data?.jobs.map((job) => <ConversationMessage key={job.id} job={job} />)
          )}
        </div>
      </div>

      {/* Reply form */}
      <div className="from-cyber-bg flex-shrink-0 bg-gradient-to-t to-transparent px-4 pt-0 pb-6">
        <form onSubmit={handleReply} className="mx-auto max-w-3xl">
          <input
            ref={replyFileInputRef}
            type="file"
            className="hidden"
            multiple
            onChange={handleReplyFileChange}
            disabled={isReplyBusy}
          />

          <div
            className={`focus-ring shadow-soft border-cyber-border bg-cyber-surface min-h-[60px] cursor-text rounded-2xl border px-4 py-3 ${
              isDraggingFiles ? "border-cyber-magenta/60 bg-cyber-magenta/5" : ""
            }`}
            onPointerDown={handleReplyContainerPointerDown}
            onDragOver={handleReplyDragOver}
            onDragLeave={handleReplyDragLeave}
            onDrop={handleReplyDrop}
          >
            <div className="flex items-center gap-2">
              <span className="text-cyber-magenta text-lg">{">"}</span>
              <textarea
                ref={replyInputRef}
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                onInput={(e) => autoResizeTextarea(e.currentTarget)}
                onPaste={handleReplyPaste}
                onKeyDown={handleReplyKeyDown}
                placeholder="Reply..."
                required
                rows={1}
                className="text-cyber-text placeholder-cyber-muted/50 flex-1 cursor-text resize-none overflow-y-auto rounded-none border-none bg-transparent text-base focus:shadow-none focus:ring-0 focus:outline-none sm:text-sm"
                autoComplete="off"
                disabled={isReplyBusy}
              />
              <button
                type="button"
                onClick={() => replyFileInputRef.current?.click()}
                disabled={isReplyBusy || isReplyRecording}
                className="text-cyber-muted hover:text-cyber-magenta inline-flex items-center justify-center p-1 transition-all"
                title="Attach files"
                aria-label="Attach files"
              >
                <Paperclip size={16} />
              </button>
              <DictationButton
                variant="magenta"
                status={dictation.status}
                isSupported={dictation.isSupported}
                disabled={isReplyBusy}
                onClick={() => {
                  void dictation.toggleRecording();
                }}
                onPointerDown={(e) => {
                  e.preventDefault();
                  focusReplyInput();
                }}
              />
              <SendButton
                variant="magenta"
                disabled={isReplyBusy || isReplyRecording || !replyMessage.trim()}
                pending={isReplySubmitting}
                onPointerDown={(e) => {
                  e.preventDefault();
                  focusReplyInput();
                }}
              />
            </div>

            <DictationStatus
              variant="magenta"
              status={dictation.status}
              elapsedMs={dictation.elapsedMs}
              level={dictation.level}
            />

            {!dictation.isSupported && (
              <div className="text-cyber-muted mt-2 text-[11px]">
                Voice dictation is unavailable in this browser.
              </div>
            )}

            {(replyFiles.length > 0 || isDraggingFiles) && (
              <div className="border-cyber-border/60 mt-2 border-t pt-2">
                {replyFiles.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {replyFiles.map((file, index) => (
                      <div
                        key={`${file.name}:${file.size}:${file.lastModified}`}
                        className="border-cyber-border bg-cyber-bg/60 text-cyber-text inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs"
                      >
                        <span className="max-w-[180px] truncate" title={file.name}>
                          {file.name}
                        </span>
                        <span className="text-cyber-muted">{formatAttachmentSize(file.size)}</span>
                        <button
                          type="button"
                          onClick={() => removeReplyFile(index)}
                          className="text-cyber-muted hover:text-cyber-red transition-all"
                          title="Remove"
                          aria-label={`Remove ${file.name}`}
                          disabled={isReplyBusy}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-cyber-muted text-xs">Drop files to attach</div>
                )}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
